/**
 * @see https://redux-toolkit.js.org/tutorials/typescript
 */
import { enableMapSet } from 'immer';
import logger from 'redux-logger';
import 'redux-persist';
import { persistReducer, persistStore } from 'redux-persist';

import {
  AnyAction,
  Dispatch,
  Middleware,
  configureStore,
} from '@reduxjs/toolkit';

import { listenerMiddleware } from './listenerMiddleware';
import { PersistConfig, basePersistConfig } from './persistConfig';
import rootReducer from './rootReducer';
import { getFeaturesFromURL, updatePersistFeatureFlag } from './utils';

import { easyEstimatorApi } from '~/api/easy-estimator-api/easyEstimatorService';
import { staticContentService } from '~/api/local-api/localService';
import { isDevMode } from '~/core/utils';

enableMapSet();

const features = getFeaturesFromURL();

updatePersistFeatureFlag(features);

const isPersistFeatureEnabled = (): boolean =>
  localStorage.getItem('features') === 'persist';

const createPersistedConfig = (isPersistEnabled: boolean): PersistConfig => ({
  ...basePersistConfig,
  ...(isPersistEnabled ? {} : { whitelist: [] }),
});

const persistConfig = createPersistedConfig(isPersistFeatureEnabled());

const persistedReducer = persistReducer(persistConfig, rootReducer);

type LoggerType = Middleware<unknown, unknown, Dispatch<AnyAction>>;

const getFabricNames = (
  fabricCanvas: fabric.Object | fabric.Group,
  prefix?: string
): string | string[] => {
  if (fabricCanvas.type === 'group') {
    const path = prefix
      ? `${prefix}/${fabricCanvas.name ?? 'nogroupname'}`
      : '';

    return (fabricCanvas as fabric.Group)
      .getObjects()
      .map((fabricObjects) => getFabricNames(fabricObjects, path).toString());
  }

  if (fabricCanvas.type === 'object') {
    const path = prefix
      ? `${prefix}/${fabricCanvas.name ?? 'noobjectname'}`
      : '';

    return getFabricNames(fabricCanvas, path);
  }

  const path = `${prefix}/${fabricCanvas.name ?? 'noobjectname'}`;

  return `${path}: ${fabricCanvas.type}`;
};

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    })
      .prepend(listenerMiddleware.middleware)
      .concat(easyEstimatorApi.middleware)
      .concat(staticContentService.middleware)
      .concat(isDevMode() ? [logger as LoggerType] : []),
  devTools: {
    stateSanitizer: <S>(state: S): S => {
      const sanitizedState = state as unknown as {
        fabric: { canvas: fabric.Object | fabric.Group };
      };

      return sanitizedState.fabric?.canvas
        ? ({
            ...sanitizedState,
            fabric: {
              ...sanitizedState.fabric,
              canvas: getFabricNames(sanitizedState.fabric.canvas),
            },
          } as S)
        : (sanitizedState as S);
    },
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const persistor = persistStore(store);
